import React, { useCallback, useState } from "react";

import { ColDef, ColGroupDef } from "ag-grid-community";
import styled from "styled-components";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { FlexWrapper } from "components/AgGrid/TableWrapper";
import { ViewSelector } from "components/AgGrid/ViewSelector";

import { Column, Row } from "components/Layout";

import { AgGridTables } from "constants/aggrid";
import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessColumnGroup,
  XeroIntegrationBusinessColId,
  XeroIntegrationBusinessDef,
} from "constants/columnDefinitions";

import { EmailDetailGridOptions } from "lib/agGrid/columnDefinitions/email";
import { getBounceCount, getErrorCount } from "lib/email";
import { selectActiveBillingRunBillingDocumentsAggridData } from "selectors";
import { useDispatch, useSelector } from "react-redux";
import { billingRunDocumentAttachmentsAction } from "actions";
import { useMountEffect } from "hooks";
import BillingDocumentSendingOptions from "./BillingDocumentSendingOptions";

const getRowId = (params: { data: BillingDocumentRow }) =>
  params.data.billingDocument.id;

const FlexTableContainer = styled.div(
  ({ theme }) => `
  position: relative;
  flex: 1;
  border-top: 2px solid ${theme.colors.gray44};
`,
);

const RightColumn = styled.div(
  ({ theme }) => `
  display: flex;
  position: relative;
  width: 50%;
  flex-direction: column;
  border-left: 2px solid ${theme.colors.gray44};
  overflow: scroll
`,
);

export const columnDefs = [
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_TYPE],
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_NUMBER],
  BusinessColumnGroup,
  XeroIntegrationBusinessDef[
    XeroIntegrationBusinessColId.XERO_INTEGRATION_BUSINESS
  ],
  BillingDocumentColumnDef[BillingDocumentColId.STATUS],
  BillingDocumentColumnDef[BillingDocumentColId.TERMS],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_AMOUNT_DIFFERENCE],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_DUE_DIFFERENCE],
  BillingDocumentColumnDef[BillingDocumentColId.EMAIL_COUNT],
  BillingDocumentColumnDef[BillingDocumentColId.ACTIVITY],
] as (ColDef | ColGroupDef)[];

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: "agMultiColumnFilter",
  enableCellChangeFlash: true,
  floatingFilter: true,
  flex: 1,
};

const LeftColumn = styled(Column)`
  resize: horizontal;
  overflow: auto;
  width: 50%;
`;

const RowClassRules = {
  "row-warning": (props: { data?: BillingDocumentRow }) => {
    const { data } = props;
    return (
      getBounceCount(data?.emails || []) + getErrorCount(data?.emails || []) > 0
    );
  },
};

interface BillingDocumentRow {
  billingDocument: BillingDocument;
  emails?: Email[];
}

interface SendProps {
  billingRunId: string | null;
  setSelectedBillingDocuments: (docs: BillingDocument[]) => void;
}

export const Send = ({
  billingRunId,
  setSelectedBillingDocuments,
}: SendProps) => {
  const [flagged, setFlagged] = useState<BillingDocumentRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const rowData = useSelector(selectActiveBillingRunBillingDocumentsAggridData);

  const [renderKey, setRenderKey] = useState(1);

  const dispatch = useDispatch();

  useMountEffect(() => {
    dispatch(billingRunDocumentAttachmentsAction.request({ id: billingRunId }));
  });

  const deselectAll = () => {
    // Use component key to force re-initialization of the aggrid component which will deselect all.
    setRenderKey(renderKey + 1);
    setFlagged([]);
  };

  const detailCellRendererParams = React.useMemo(() => {
    return {
      detailGridOptions: EmailDetailGridOptions,
      getDetailRowData: (params: {
        data: BillingDocumentRow;
        successCallback: (rows: Email[]) => void;
      }) => {
        params.successCallback(params.data.emails || []);
      },
    };
  }, []);

  const onRowSelectionChanged = useCallback(
    (selectedRows: BillingDocumentRow[]) => {
      setFlagged(selectedRows);
      setSelectedBillingDocuments(selectedRows.map(r => r.billingDocument));
    },
    [setSelectedBillingDocuments],
  );

  return (
    <>
      <Row fullWidth margin={1}>
        <ViewSelector
          tableName={AgGridTables.BILLING_DOCUMENTS_SEND}
          isDirty={isDirty}
        />
      </Row>
      <Row full>
        <LeftColumn>
          <FlexTableContainer>
            <AgGridTable
              columnDefs={columnDefs}
              getRowId={getRowId}
              rowData={rowData}
              showGlobalSearchFilter={false}
              hideHeader
              WrapperComponent={FlexWrapper}
              pagination={false}
              rowSelection={RowSelectionMode.MULTIPLE}
              rowsSelectable
              onRowSelectionChanged={onRowSelectionChanged}
              masterDetail
              detailCellRendererParams={detailCellRendererParams}
              defaultColDef={defaultColDef}
              key={renderKey}
              tableName={AgGridTables.BILLING_DOCUMENTS_SEND}
              rowClassRules={RowClassRules}
              onSetViewDirty={setIsDirty}
            />
          </FlexTableContainer>
        </LeftColumn>
        <RightColumn>
          <BillingDocumentSendingOptions
            billingDocuments={flagged}
            onClose={deselectAll}
          />
        </RightColumn>
      </Row>
    </>
  );
};
